import { useEffect, useState } from 'react';
import { SelectItem } from '@/components/ui/select';
import { SelectField } from './SelectField';
import { useNode } from '@craftjs/core';
import { useFonts } from '@/context/FontProvider';
import { cn } from '@/lib/utils';
import type { CountdownProps } from '@builder4/shared/components/Countdown';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';

const NodeCountdownFontWeightSettings = ({
  fontFamily,
  className = 'max-w-[216px]',
  type,
}: {
  fontFamily?: { family: string; variants: string[] };
  className?: string;
  type: 'label' | 'number';
}) => {
  const { fontManager } = useFonts();

  const {
    props,
    actions: { setProp },
    displayName,
  } = useNode((node) => ({
    node: node,
    id: node.id,
    props: node.data.props,
    displayName: node.data.displayName,
  }));

  const { setBPProp, getBPProp } = useBreakpointAwareProp(displayName);

  const labelFontWeight = getBPProp(props, 'labelFontWeight');
  const numberFontWeight = getBPProp(props, 'numberFontWeight');

  const [weights, setWeights] = useState<
    Array<{ label: string; value: string; weight: string; style: string }>
  >([]);

  useEffect(() => {
    if (fontFamily && fontManager) {
      const weights = fontManager.getAvailableWeights(fontFamily.family);
      // Filter out italic variants per requirement
      const nonItalicWeights = weights.filter((weight) => !/italic/i.test(weight.value));
      setWeights(nonItalicWeights);
    }
  }, [fontFamily, fontManager]);

  const handleWeightChange = (variant: string) => {
    const weight = weights.find((weight) => weight.value === variant);
    if (!weight) return;
    if (type === 'label') {
      setProp((props: CountdownProps) => {
        setBPProp(props, 'labelFontWeight', weight.weight);
        setBPProp(props, 'labelFontStyle', weight.style);
      }, 500);
    } else {
      setProp((props: CountdownProps) => {
        setBPProp(props, 'numberFontWeight', weight.weight);
        setBPProp(props, 'numberFontStyle', weight.style);
      }, 500);
    }
  };

  const getFontVariant = () => {
    let weights = fontManager?.getAvailableWeights(props.fontFamily) || []; //sorted weights
    let fontWeight = type === 'label' ? labelFontWeight : numberFontWeight;
    if (!fontWeight || fontWeight === 'normal') {
      fontWeight = '400';
    }
    if (fontWeight === 'bold') {
      //get largest weight that is bold, as getAvailableWeights returns sorted weights so no need to sort again
      weights = weights.filter((weight) => parseInt(weight.weight) >= 400);
      if (weights.length > 0) {
        fontWeight = weights[weights.length - 1].weight;
      }
    }

    const weightObject = weights.find((v) => v.weight === fontWeight);
    return weightObject?.value || 'regular';
  };

  return (
    <SelectField
      onValueChange={handleWeightChange}
      className={cn(className)}
      defaultValue={getFontVariant()}
      size="small"
      text="Weight"
      truncate={false}
      dataTestId="weight-selection"
    >
      {weights.length > 0 ? (
        weights.map((weight) => (
          <SelectItem key={weight.value} value={weight.value}>
            {weight.label}
          </SelectItem>
        ))
      ) : (
        <SelectItem value="loading" disabled>
          Loading...
        </SelectItem>
      )}
    </SelectField>
  );
};

export default NodeCountdownFontWeightSettings;
